/**
 * Reading facsimile pages and imported fonts back off a stored document.
 *
 * Forgiving, as every reader in this application is: a field of the wrong
 * type falls back on its own, a malformed word or frame is dropped and its
 * neighbours kept. A page that loses one word is still a page.
 */

import { isJsonArray, isJsonObject, readArray, readString, type JsonValue } from '@/domain/json'
import type { ImportedFont } from '@/domain/facsimile/fonts'
import type {
  EditedRun,
  FacsimileFrame,
  FacsimileLine,
  FacsimileRun,
  FrameAlign,
  FrameBox,
} from '@/domain/facsimile/types'

const ALIGNS: readonly FrameAlign[] = ['left', 'right', 'center', 'justify']
const COLOUR = /^#[0-9a-f]{6}$/i

function numberOr(value: JsonValue | undefined, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function readColour(value: JsonValue | undefined): string {
  return typeof value === 'string' && COLOUR.test(value) ? value : '#000000'
}

function readAnchors(value: JsonValue | undefined): [number, number][] {
  if (!isJsonArray(value)) return []
  return value.flatMap((pair) =>
    isJsonArray(pair) && typeof pair[0] === 'number' && typeof pair[1] === 'number'
      ? [[pair[0], pair[1]] as [number, number]]
      : [],
  )
}

function readRun(value: JsonValue, lineBaseline: number): FacsimileRun | null {
  if (!isJsonObject(value) || typeof value['text'] !== 'string') return null
  return {
    baseline: numberOr(value['baseline'], lineBaseline),
    tracking: numberOr(value['tracking']),
    anchors: readAnchors(value['anchors']),
    ligatures: isJsonArray(value['ligatures'])
      ? value['ligatures'].filter((index): index is number => typeof index === 'number')
      : [],
    text: value['text'],
    x: numberOr(value['x']),
    width: numberOr(value['width']),
    font: readString(value, 'font'),
    size: numberOr(value['size'], 10),
    colour: readColour(value['colour']),
  }
}

function readLine(value: JsonValue): FacsimileLine | null {
  if (!isJsonObject(value)) return null
  const baseline = numberOr(value['baseline'])
  const runs = readArray(value, 'runs')
    .map((run) => readRun(run, baseline))
    .filter((run) => run !== null)
  return runs.length > 0 ? { baseline, runs } : null
}

function readEdited(value: JsonValue | undefined): EditedRun[] | null {
  if (!isJsonArray(value)) return null
  return value.flatMap((item) =>
    isJsonObject(item) && typeof item['text'] === 'string'
      ? [
          {
            text: item['text'],
            font: readString(item, 'font'),
            size: numberOr(item['size'], 10),
            colour: readColour(item['colour']),
          },
        ]
      : [],
  )
}

function readBox(value: JsonValue | undefined): FrameBox {
  const numbers = isJsonArray(value) ? value.map((item) => numberOr(item)) : []
  return [numbers[0] ?? 0, numbers[1] ?? 0, numbers[2] ?? 0, numbers[3] ?? 0]
}

function readFrame(value: JsonValue): FacsimileFrame | null {
  if (!isJsonObject(value)) return null
  const align = readString(value, 'align') as FrameAlign
  return {
    id: readString(value, 'id'),
    box: readBox(value['box']),
    align: ALIGNS.includes(align) ? align : 'left',
    lines: readArray(value, 'lines')
      .map(readLine)
      .filter((line) => line !== null),
    edited: readEdited(value['edited']),
    ...readRotation(value['rotation']),
  }
}

/** A frame's turn: a quarter turn either way, or nothing. Anything else is drawn upright. */
function readRotation(value: JsonValue | undefined): { rotation?: number } {
  return value === 90 || value === -90 ? { rotation: value } : {}
}

export function readFrames(stored: JsonValue): FacsimileFrame[] {
  return readArray(stored, 'frames')
    .map(readFrame)
    .filter((frame): frame is FacsimileFrame => frame !== null && frame.id !== '')
}

/** A data URL for a font file, and nothing else: it ends up in a stylesheet. */
function readFontSource(value: JsonValue | undefined): string {
  return typeof value === 'string' && /^data:font\/[a-z0-9.+-]+;base64,[A-Za-z0-9+/=]+$/.test(value)
    ? value
    : ''
}

/** A family name, which is written into CSS and a font URL: letters, digits and spaces only. */
function readFamily(value: JsonValue, key: string): string {
  const family = readString(value, key)
  return /^[A-Za-z0-9 ]{1,60}$/.test(family) ? family : ''
}

function readFont(value: JsonValue): ImportedFont | null {
  if (!isJsonObject(value) || readString(value, 'key') === '') return null
  return {
    key: readString(value, 'key'),
    family: readString(value, 'family'),
    weight: numberOr(value['weight'], 400),
    italic: value['italic'] === true,
    embedded: readFontSource(value['embedded']),
    characters: readString(value, 'characters'),
    openFamily: readFamily(value, 'openFamily'),
    substitute: readFamily(value, 'substitute'),
    uploaded: readFontSource(value['uploaded']),
  }
}

export function readImportedFonts(values: readonly JsonValue[]): ImportedFont[] {
  return values.map(readFont).filter((font): font is ImportedFont => font !== null)
}
